import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Match, Pairing, MatchResult } from '../types/match';
import { Player } from '../types/player';
import { MatchService } from '../services/matchService';
import { PairingAlgorithm } from '../utils/pairingAlgorithm';

interface MatchState {
  // 状态
  currentPairing: Pairing | null;
  inProgressMatch: Match | null; // 进行中的比赛
  matchHistory: Match[];
  isLoading: boolean;
  error: string | null;
  
  // 分页状态
  historyPage: number;
  hasMoreHistory: boolean;

  // 操作
  generatePairing: (players: Player[]) => void;
  generateMultiplePairings: (players: Player[], count?: number) => Pairing[];
  generateBalancedPairing: (players: Player[]) => Promise<void>;
  startMatch: (queueSnapshot: Player[]) => Promise<void>; // 新增：开始比赛
  completeMatch: (matchId: string, result: MatchResult) => Promise<void>; // 新增：完成比赛
  loadInProgressMatch: () => Promise<void>; // 新增：加载进行中的比赛
  saveMatch: (result: MatchResult, queueSnapshot: Player[]) => Promise<void>; // 保留兼容性
  loadMatchHistory: (page?: number) => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  clearCurrentPairing: () => void;
  clearError: () => void;

  // 计算属性
  getCurrentMatchPlayers: () => Player[];
  getPlayerMatches: (playerId: string) => Promise<Match[]>;
}

export const useMatchStore = create<MatchState>()(
  subscribeWithSelector((set, get) => ({
    // 初始状态
    currentPairing: null,
    inProgressMatch: null,
    matchHistory: [],
    isLoading: false,
    error: null,
    historyPage: 0,
    hasMoreHistory: true,

    // 生成随机配对
    generatePairing: (players: Player[]) => {
      try {
        const pairing = PairingAlgorithm.generateRandomPairing(players);
        set({ currentPairing: pairing, error: null });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '生成配对失败',
        });
      }
    },

    // 生成多个配对选项
    generateMultiplePairings: (players: Player[], count = 3) => {
      try {
        return PairingAlgorithm.generateMultiplePairings(players, count);
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '生成多个配对失败',
        });
        return [];
      }
    },

    // 生成平衡配对
    generateBalancedPairing: async (players: Player[]) => {
      set({ isLoading: true, error: null });

      try {
        // 获取历史配对记录
        const history = await MatchService.getMatchHistory(50);
        const historyPairings = history.map(match => match.pairing);
        
        const pairing = PairingAlgorithm.generateBalancedPairing(players, historyPairings);
        
        set({
          currentPairing: pairing,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '生成平衡配对失败',
          isLoading: false,
        });
      }
    },

    // 开始比赛（保存为进行中状态）
    startMatch: async (queueSnapshot: Player[]) => {
      const { currentPairing } = get();
      
      if (!currentPairing) {
        set({ error: '没有当前配对信息' });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        const match = await MatchService.startMatch(
          currentPairing,
          queueSnapshot
        );

        set({
          inProgressMatch: match,
          currentPairing: null,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '开始比赛失败',
          isLoading: false,
        });
      }
    },

    // 完成比赛（更新结果）
    completeMatch: async (matchId: string, result: MatchResult) => {
      set({ isLoading: true, error: null });

      try {
        const match = await MatchService.completeMatch(matchId, result);

        set((state) => ({
          matchHistory: [match, ...state.matchHistory],
          inProgressMatch: null,
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '完成比赛失败',
          isLoading: false,
        });
      }
    },

    // 加载进行中的比赛
    loadInProgressMatch: async () => {
      set({ isLoading: true, error: null });

      try {
        const match = await MatchService.getInProgressMatch();
        
        set({
          inProgressMatch: match,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '加载进行中的比赛失败',
          isLoading: false,
        });
      }
    },

    // 保存比赛结果（旧方法，保持兼容性）
    saveMatch: async (result: MatchResult, queueSnapshot: Player[]) => {
      const { currentPairing } = get();
      
      if (!currentPairing) {
        set({ error: '没有当前配对信息' });
        return;
      }

      set({ isLoading: true, error: null });

      try {
        const match = await MatchService.saveMatch(
          currentPairing,
          result,
          queueSnapshot
        );

        set((state) => ({
          matchHistory: [match, ...state.matchHistory],
          currentPairing: null,
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '保存比赛结果失败',
          isLoading: false,
        });
      }
    },

    // 加载比赛历史
    loadMatchHistory: async (page = 0) => {
      set({ isLoading: true, error: null });

      try {
        const limit = 20;
        const offset = page * limit;
        const history = await MatchService.getMatchHistory(limit, offset);

        set({
          matchHistory: page === 0 ? history : get().matchHistory.concat(history),
          historyPage: page,
          hasMoreHistory: history.length === limit,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '加载比赛历史失败',
          isLoading: false,
        });
      }
    },

    // 加载更多历史记录
    loadMoreHistory: async () => {
      const { historyPage, hasMoreHistory, isLoading } = get();
      
      if (!hasMoreHistory || isLoading) return;

      await get().loadMatchHistory(historyPage + 1);
    },

    // 清除当前配对
    clearCurrentPairing: () => {
      set({ currentPairing: null });
    },

    // 清除错误
    clearError: () => {
      set({ error: null });
    },

    // 获取当前比赛的所有玩家
    getCurrentMatchPlayers: () => {
      const { currentPairing } = get();
      if (!currentPairing) return [];
      
      return [...currentPairing.team1, ...currentPairing.team2];
    },

    // 获取特定玩家的比赛记录
    getPlayerMatches: async (playerId: string) => {
      try {
        return await MatchService.getPlayerMatches(playerId);
      } catch (error) {
        set({
          error: error instanceof Error ? error.message : '获取玩家比赛记录失败',
        });
        return [];
      }
    },
  }))
);

// 自动验证当前配对的有效性
useMatchStore.subscribe(
  (state) => state.currentPairing,
  (pairing) => {
    if (pairing) {
      const validation = PairingAlgorithm.validatePairing(pairing);
      if (!validation.isValid) {
        useMatchStore.setState({
          error: `配对无效: ${validation.errors.join(', ')}`,
          currentPairing: null,
        });
      }
    }
  }
);